import { useEffect, useState } from "react";

interface UseDragReorderOptions<T> {
  items: T[];
  /**
   * Called once on drag end with the final order. Throw (or reject) to revert
   * the list to the incoming `items` — show your own error toast in the caller.
   */
  onCommit: (items: T[]) => void | Promise<void>;
}

// HTML5 drag-to-reorder for table rows (DESIGN.md › Tables › Reorderable rows).
// Owns the optimistic local order + the dataTransfer bookkeeping (move cursor,
// not the copy `+` cursor). Spread `getRowProps(index)` onto each `TableRow`
// and render a leading `DragHandleCell`.
export function useDragReorder<T>({ items, onCommit }: UseDragReorderOptions<T>) {
  const [ordered, setOrdered] = useState(items);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  useEffect(() => {
    setOrdered(items);
  }, [items]);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.effectAllowed = "move";
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (draggedIndex === null || draggedIndex === index) return;
    setOrdered((prev) => {
      const next = [...prev];
      const [dragged] = next.splice(draggedIndex, 1);
      next.splice(index, 0, dragged);
      return next;
    });
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    if (draggedIndex === null) return;
    setDraggedIndex(null);
    try {
      await onCommit(ordered);
    } catch {
      setOrdered(items);
    }
  };

  const getRowProps = (index: number) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => handleDragStart(e, index),
    onDragOver: (e: React.DragEvent) => handleDragOver(e, index),
    onDragEnd: handleDragEnd,
    onDrop: (e: React.DragEvent) => e.preventDefault(),
  });

  return { ordered, draggedIndex, getRowProps };
}
